import { Link, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useRef } from "react";

const PREFIX = "Disclaimers";

const classes = {
  Disclaimers: `${PREFIX}-Disclaimers`,
  listItem: `${PREFIX}-listItem`,
  IndentedList: `${PREFIX}-IndentedList`,
  HelpTopic: `${PREFIX}-HelpTopic`,
  HelpTopicNormalWeight: `${PREFIX}-HelpTopicNormalWeight`,
  IndentedIndented: `${PREFIX}-IndentedIndented`,
  Pointer: `${PREFIX}-Pointer`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.Disclaimers}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    paddingBottom: 0,
    lineHeight: "32px",
  },

  [`& .${classes.listItem}`]: {
    "margin": 0,
    "& h3": {
      paddingBottom: 0,
    },
  },

  [`& .${classes.IndentedList}`]: {
    color: theme.palette.primary.main,
    marginLeft: 40,
    lineHeight: "24px",
  },

  [`& .${classes.HelpTopic}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
  },

  [`& .${classes.HelpTopicNormalWeight}`]: {
    marginLeft: 8,
    color: theme.palette.primary.main,
    fontWeight: "normal",
  },

  [`& .${classes.IndentedIndented}`]: {
    marginLeft: 80,
    color: theme.palette.primary.main,
  },

  [`&.${classes.Pointer}`]: {
    "& a": {
      cursor: "pointer",
    },
  },
}));

const handleScroll = (ref: any) => {
  ref.scrollIntoView({ behavior: "smooth" });
};

export default function Disclaimers({ title }: { title?: React.JSX.Element }) {
  const Disclaimers = useRef(null);
  const Data_Sources = useRef(null);
  const Demographic_Information = useRef(null);
  const Computational_Methodologies = useRef(null);
  const Averaging = useRef(null);
  const Quarterly_Averages = useRef(null);
  const Merger_Adjustment = useRef(null);
  const Reporting_Basis = useRef(null);
  const Peer_Group_Data = useRef(null);
  const Comparability = useRef(null);
  const Deriving_Income_And_Expense = useRef(null);
  const OTS_Thrift_Financial_Reports = useRef(null);
  const Return_To_Index = useRef(null);

  const returnLink = (
    <Link
      underline="always"
      onClick={() => {
        handleScroll(Return_To_Index.current);
      }}
    >
      Return To Index
    </Link>
  );

  const refLink = (ref: any, name: string) => {
    const id = `${name.toLowerCase()}Link`;
    return (
      <Link
        id={id}
        underline="always"
        onClick={() => {
          handleScroll(ref.current);
        }}
      >
        {name}
      </Link>
    );
  };
  return (
    <Root className={classes.Pointer} ref={Return_To_Index}>
      {title}
      <Typography variant="h2" className={classes.Disclaimers}>
        Index of Contents
      </Typography>
      <ul className={classes.listItem}>
        <li>
          <ul style={{ padding: "0 0 1rem 0" }}>
            <Typography variant="h3">
              {refLink(Disclaimers, "Disclaimers")}
            </Typography>

            <li className={classes.IndentedList}>
              {refLink(Data_Sources, "Data Sources")}
            </li>

            <li className={classes.IndentedList}>
              {refLink(Demographic_Information, "Demographic Information")}
            </li>
          </ul>
          <ul style={{ padding: "0 0 1rem 0" }}>
            <Typography variant="h3">
              {refLink(
                Computational_Methodologies,
                "Computational Methodologies",
              )}
            </Typography>
            <li className={classes.IndentedList}>
              {refLink(Averaging, "Averaging")}
            </li>
            <li className={classes.IndentedList}>
              {refLink(Quarterly_Averages, "Quarterly Averages")}
            </li>
            <li className={classes.IndentedList}>
              {refLink(Merger_Adjustment, "Merger Adjustment")}
            </li>
            <li className={classes.IndentedList}>
              {refLink(Reporting_Basis, "Reporting Basis")}
            </li>
            <li className={classes.IndentedList}>
              {refLink(Peer_Group_Data, "Peer Group Data")}
            </li>
            <li className={classes.IndentedList}>
              {refLink(
                Comparability,
                "Comparability with Other Sources of Information",
              )}
            </li>
            <li className={classes.IndentedList}>
              {refLink(
                Deriving_Income_And_Expense,
                "Deriving Quarterly and Year-to-date Income and Expense Amounts",
              )}
            </li>
            <li className={classes.IndentedList}>
              {refLink(
                OTS_Thrift_Financial_Reports,
                "Reporting Differences for OTS Thrift Financial Reports",
              )}
            </li>
          </ul>
        </li>
      </ul>
      <div ref={Disclaimers}>
        <Typography variant="h3">Disclaimers</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          Although every attempt has been made to ensure the reliability of the
          information contained herein, some inaccuracies may exist. For this
          reason, the Federal Deposit Insurance Corporation (FDIC) neither
          assumes responsibility for nor certifies to the accuracy of any of the
          reported data.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          Further, these data do not indicate approval or disapproval of any
          particular institution's performance and are not to be construed as a
          rating of any institution by federal regulators.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          All notes contained within are an integral part of this publication
          and provide information regarding the comparability of source data and
          reporting differences over time. Users are cautioned that any
          conclusions, which they may draw from FDIC Bank Data and Statistics,
          are their own and are not to be attributed to the federal regulators.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Data_Sources}>
        <Typography variant="h3">Data Sources</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          The financial information appearing in BankFind Suite’s Financial
          Reporting (FR) and Peer Group Comparisons (PGC) is obtained primarily
          from the Federal Financial Institution Examination Council (FFIEC)
          Call Reports and before 2012 the Office of Thrift Supervision (OTS)
          Thrift Financial Reports submitted by all FDIC-insured depository
          institutions.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          All data presented reflect the highest level of consolidation (e.g.,
          domestic and foreign operations) unless otherwise indicated. This
          information is stored on and retrieved from the FDIC's Research
          Information System database.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Demographic_Information}>
        <Typography variant="h3">Demographic Information</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          The demographic information provided in BankFind Suite (includes bank
          name, address, primary federal regulator and insurance fund
          membership) is updated on a weekly basis. Check the report to see when
          the demographic information was last updated. Financial institutions
          may change their demographic characteristics at any time throughout
          the year.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          Because the demographic information on FDIC's structure files is
          obtained from several regulatory sources, the Bank List may not always
          reflect recent changes while updates are being processed.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Computational_Methodologies}>
        <Typography variant="h3">Computational Methodologies</Typography>
      </div>
      <div ref={Averaging}>
        <Typography className={classes.IndentedList} variant="h3">
          Averaging
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          All condition and performance ratios represent weighted averages,
          i.e., the sum of the individual numerator values divided by the sum of
          individual denominator values. All denominator figures (e.g., assets,
          liabilities, and capital) used in calculating performance ratios
          represent average amounts for the period. Year-to-date calculations
          include the beginning-of-period amount plus end-of-period amount plus
          any interim periods, divided by the total number of periods. Quarterly
          calculations include the beginning-of-period amount plus end-of-period
          amount, divided by the total number of periods.
        </Typography>
        <Typography className={classes.IndentedIndented} variant="h3">
          Example - Total Average Assets
        </Typography>
        <Typography
          className={classes.IndentedIndented}
          variant="body2"
          color="primary.main"
          component="div"
        >
          Year-to-date averages:
          <ul>
            <li>
              March reporting period - (December assets + March assets) / 2
            </li>
            <li>
              June reporting period - (December assets + March assets + June
              assets) / 3
            </li>
            <li>
              September reporting period - (December assets + March assets +
              June assets + September assets) / 4
            </li>
            <li>
              December reporting period - (Previous December assets + March
              assets + June assets + September assets + December assets) / 5
            </li>
          </ul>
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Quarterly_Averages}>
        <Typography variant="h3">Quarterly averages:</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          All reporting periods - (Previous quarter assets + current quarter
          assets) / 2
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Merger_Adjustment}>
        <Typography variant="h3">Merger Adjustment</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          There are two types of accounting treatment for mergers and
          acquisitions that can affect banks' financial reporting.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          After "pooling-of-interest" mergers, income and expense totals reflect
          the year-to-date results of the combined entity. In computing
          performance ratios where "pooling-of-interest" accounting is used, the
          denominator values (e.g., assets, liabilities, capital) of the
          acquired institution are included for all prior periods used in
          computing the denominator average. Prior period income and expense
          figures of the acquired institution are used in computing numerator
          values for quarterly performance ratios.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          For "purchase accounting" or "push-down" acquisitions, income and
          expenses attributable to the acquired institution are reset to zero
          effective with the date of the acquisition. For "purchase accounting"
          acquisitions, no adjustments are made. Merger/acquisition accounting
          and certain transactions that can accompany these transactions can
          have the effect of temporarily distorting performance measures for
          individual institutions.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Reporting_Basis}>
        <Typography variant="h3">Reporting Basis</Typography>
        <Typography className={classes.IndentedList} variant="body2">
          All data are collected and presented based on the location of each
          reporting institution's main office.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          Reported data may include assets and liabilities located outside of
          the reporting institution's home state. In addition, institutions may
          change their charters, resulting in an inter-industry migration, e.g.,
          savings institutions can convert to commercial banks, or commercial
          banks may convert to savings institutions.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          These situations can affect state and regional statistics.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Peer_Group_Data}>
        <Typography variant="h3">Peer Group Data</Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          Group data are calculated by using the weighted average approach.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          The data are summed by groups and presented accordingly.
        </Typography>
        <Typography
          className={classes.IndentedList}
          variant="body2"
          color="primary.main"
        >
          If ratios are presented, the ratio uses the summed data in both
          numerator and denominator to calculate the appropriate ratio.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Comparability}>
        <Typography variant="h3" color="primary.main">
          Comparability with Other Sources of Information
        </Typography>
        <Typography variant="body2" color="primary.main">
          Ratio calculations are consistent with those used in the FDIC
          Quarterly Banking Profile.
        </Typography>
        <Typography variant="body2" color="primary.main">
          Note that various publications distributed by both government and
          private sources may use different formulations or calculations to
          derive similar performance measures.
        </Typography>
        <Typography variant="body2" color="primary.main">
          This could cause minor differences in data depending on the
          publication.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={Deriving_Income_And_Expense}>
        <Typography variant="h3">
          Deriving Quarterly and Year-to-date Income and Expense Amounts
        </Typography>
        <Typography variant="body2" color="primary.main">
          As a result of Dodd-Frank legislation, all insured institutions are
          required to file a Call Report as of the March 2012 reporting cycle.
        </Typography>
        <Typography variant="body2" color="primary.main">
          Savings institutions that filed Thrift Financial Reports before 2012
          reported income and expenses on a quarterly basis, i.e. for three
          months. For these institutions, adding together quarterly amounts
          derives year-to-date amounts. For example, income for the six months
          ending June 30 was calculated by adding together income for the first
          quarter and the second quarter.
        </Typography>
        <Typography variant="body2" color="primary.main">
          In contrast, banks Call Reports disclose income and expenses on a
          year-to-date basis. Consequently, quarterly figures are derived from
          year-to-date figures by subtracting amounts reported on the previous
          Call Report. For example, second quarter net income is derived by
          subtracting the amount reported on the previous (March 31) Call Report
          from the amount reported on the current (June 30) Call Report.
        </Typography>
        <Typography variant="body2" color="primary.main">
          Where institutions engage in "pooling-of-interest" mergers, previous
          income statements filed by each of the entities before the merger are
          added together and the resulting total is subtracted from the current
          amount reported by the remaining institution. For example, if Bank A
          acquires Bank B as of May 15 and accounts for the acquisition as a
          "pooling-of-interest" merger, income and expense amounts reported by
          each institution for the first quarter are added together. This amount
          is then subtracted from the year-to-date amount reported by Bank A as
          of June 30 to derive income and expense for the second quarter.
        </Typography>
        <Typography variant="body2" color="primary.main">
          For purchase accounting mergers, no adjustments are made. For example,
          if Bank A acquires Bank B as of close of business on May 15 and
          accounts for the acquisition as a purchase, income and expenses for
          the second quarter will only include amounts recorded for three months
          for Bank A and for six weeks for Bank B. For the same reason, no
          adjustments are made for "push down" (purchase method accounting)
          acquisitions.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
      <div ref={OTS_Thrift_Financial_Reports}>
        <Typography variant="h3">
          Reporting Differences for OTS Thrift Financial Reports
        </Typography>
        <Typography variant="body2" color="primary.main">
          Certain adjustments are made to OTS Thrift Financial Reports to
          provide closer conformance with the reporting and accounting
          requirements of the FFIEC Call Reports.
        </Typography>
        <Typography variant="body2" color="primary.main">
          Beginning with June 1996, the Thrift Financial Report is completed on
          a fully consolidated basis, with the exception of subsidiary
          depository institutions being reported on the equity method of
          accounting. Prior to this time, reporting reflected the consolidation
          of the parent thrift with finance subsidiaries. All other subsidiaries
          were reported on an equity or cost basis. Some accounting differences
          between Thrift Financial Reports and Call Reports between pre-June
          1996 and post-June 1996 exist, such as asset sales with recourse, for
          which the data necessary to reconcile these differences are not
          reported in the Thrift Financial Report.
        </Typography>
      </div>
      <Typography
        variant="body2"
        style={{ textAlign: "right", paddingBottom: 16 }}
      >
        {returnLink}
      </Typography>
    </Root>
  );
}
